import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  useReports,
  useUpdateReport,
  useCloneReport,
  useDeleteReport,
  type ReportSummary,
} from '../api/reports.js';
import { useReportTemplate, useReportTemplates } from '../api/report-templates.js';
import type { ReportTemplateSummary } from '../api/types.js';
import {
  useCreateFolder,
  useDeleteFolder,
  useFolders,
  useMoveFolder,
  useRenameFolder,
  type Folder,
} from '../api/folders.js';
import { useMoveItem } from '../api/reorder.js';
import { ReportsFolderTree } from './reports-instances/ReportsFolderTree.js';
import { MoveToFolderDialog } from './reports/MoveToFolderDialog.js';
import { RenderPanel } from './reports/RenderPanel.js';
import { SharePanel } from './reports/SharePanel.js';
import { useTemplateRuns } from '../api/render.js';
import { useSchedule } from '../api/schedules.js';
import { PromptDialog } from '../components/dialogs/PromptDialog.js';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog.js';
import { useListSidebar, useListSidebarWidth } from '../shell/useAppChrome.js';

/**
 * Reports — the operator view.
 *
 * A Report is a Template bound to a named run config: default params,
 * folder placement, audience + page overrides, delivery. Templates are
 * authored under /templates; this page is where customer-bound reports
 * live, get scheduled, cloned, and run.
 *
 * Layout mirrors /templates for consistency: folder tree on the left
 * with reports as leaves; detail pane on the right for the selected
 * report, with tabs for Overview, Defaults, Schedule, Delivery, Runs.
 */
export function ReportsPage(): JSX.Element {
  const reports = useReports();
  const foldersQ = useFolders('reports');
  const templates = useReportTemplates();
  const { id: urlId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(urlId ?? null);
  const listSidebar = useListSidebar('reports');
  const listSidebarWidth = useListSidebarWidth('reports');

  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  function toggleFolder(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const [moveTarget, setMoveTarget] = useState<MoveTargetState | null>(null);
  const [promptDialog, setPromptDialog] = useState<PromptState | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmState | null>(null);

  useEffect(() => {
    if (urlId && urlId !== selected) setSelected(urlId);
  }, [urlId]); // eslint-disable-line react-hooks/exhaustive-deps

  function selectReport(id: string | null): void {
    setSelected(id);
    navigate(id ? `/reports/${id}${window.location.search}` : '/reports');
  }

  const renameFolderM = useRenameFolder();
  const moveFolderM = useMoveFolder();
  const deleteFolderM = useDeleteFolder();
  const createFolderM = useCreateFolder();
  const updateReportM = useUpdateReport();
  const cloneReportM = useCloneReport();
  const deleteReportM = useDeleteReport();

  // Folder actions — shared pattern with TemplatesPage.
  function handleRenameFolder(f: Folder): void {
    setPromptDialog({
      title: 'Rename folder',
      label: 'Folder name',
      initialValue: f.name,
      submitLabel: 'Rename',
      onSubmit: async (newName) => {
        await renameFolderM.mutateAsync({ id: f.id, name: newName });
        setPromptDialog(null);
      },
    });
  }
  function handleDeleteFolder(f: Folder): void {
    setConfirmDialog({
      title: 'Delete folder',
      message: (
        <>
          Delete <strong>{f.name}</strong>? Reports inside move to top level; nested folders are
          deleted. This action cannot be undone.
        </>
      ),
      confirmLabel: 'Delete folder',
      onConfirm: async () => {
        await deleteFolderM.mutateAsync(f.id);
        setConfirmDialog(null);
      },
    });
  }
  function handleNewFolder(parent: Folder | null): void {
    setPromptDialog({
      title: parent ? `New folder inside "${parent.name}"` : 'New top-level folder',
      label: 'Folder name',
      placeholder: 'e.g. Finance',
      submitLabel: 'Create',
      onSubmit: async (name) => {
        await createFolderM.mutateAsync({ name, type: 'reports', parentId: parent?.id ?? null });
        setPromptDialog(null);
      },
    });
  }

  // Report actions.
  function handleRenameReport(r: ReportSummary): void {
    setPromptDialog({
      title: 'Rename report',
      label: 'Report name',
      initialValue: r.name,
      submitLabel: 'Rename',
      onSubmit: async (name) => {
        await updateReportM.mutateAsync({ id: r.id, patch: { name } });
        setPromptDialog(null);
      },
    });
  }
  function handleDeleteReport(r: ReportSummary): void {
    setConfirmDialog({
      title: 'Delete report',
      message: (
        <>
          Delete <strong>{r.name}</strong>? The underlying template is not touched, but this
          report's defaults, schedule, and delivery configuration are lost. This action cannot be
          undone.
        </>
      ),
      confirmLabel: 'Delete report',
      onConfirm: async () => {
        await deleteReportM.mutateAsync(r.id);
        if (selected === r.id) selectReport(null);
        setConfirmDialog(null);
      },
    });
  }
  function handleCloneReport(r: ReportSummary): void {
    setPromptDialog({
      title: 'Duplicate report',
      description:
        'The clone copies defaults, folder, audience, and delivery. Schedule is not copied.',
      label: 'New report name',
      initialValue: `${r.name} (copy)`,
      submitLabel: 'Create copy',
      onSubmit: async (name) => {
        const created = await cloneReportM.mutateAsync({ id: r.id, name });
        setPromptDialog(null);
        selectReport(created.id);
      },
    });
  }
  function handleRunReport(r: ReportSummary): void {
    // v1: take the user to the template's Render tab with the defaults
    // pre-filled via URL state. The full Render-instance page comes next.
    navigate(`/templates/${r.templateId}?tab=render&reportId=${r.id}`);
  }

  function handleMoveFolder(f: Folder): void {
    setMoveTarget({ kind: 'folder', folder: f });
  }
  function handleMoveReport(r: ReportSummary): void {
    setMoveTarget({ kind: 'report', report: r });
  }

  // Reorder within the report's folder. The server-side ordering comes
  // back via the reports list (ORDER BY sort_order), so we filter the
  // current list to siblings and ask useMoveItem to swap with the
  // adjacent one.
  const moveItem = useMoveItem();
  function handleReorderReport(r: ReportSummary, direction: 'up' | 'down'): void {
    const all = reports.data ?? [];
    const siblings = all
      .filter((x) => (x.folderId ?? null) === (r.folderId ?? null))
      .map((x) => ({ id: x.id }));
    moveItem('reports', r.id, direction, siblings);
  }
  function handleReorderFolder(f: Folder, direction: 'up' | 'down'): void {
    const all = foldersQ.data ?? [];
    const siblings = all
      .filter((x) => (x.parentId ?? null) === (f.parentId ?? null) && x.type === f.type)
      .map((x) => ({ id: x.id }));
    moveItem('folders', f.id, direction, siblings);
  }

  // Drag-and-drop moves — direct (no dialog). Top level = null target.
  async function handleDropReportToFolder(
    reportId: string,
    folderId: string | null,
  ): Promise<void> {
    await updateReportM.mutateAsync({ id: reportId, patch: { folderId } });
  }
  async function handleDropFolderToFolder(
    folderId: string,
    parentId: string | null,
  ): Promise<void> {
    if (folderId === parentId) return;
    try {
      await moveFolderM.mutateAsync({ id: folderId, parentId });
    } catch {
      // The server rejects cycles (dropping a folder into its own
      // descendant). Swallow — the tree just doesn't move.
    }
  }
  async function handleMoveTargetChosen(newParentId: string | null): Promise<void> {
    if (!moveTarget) return;
    if (moveTarget.kind === 'folder') {
      await moveFolderM.mutateAsync({ id: moveTarget.folder.id, parentId: newParentId });
    } else {
      await updateReportM.mutateAsync({
        id: moveTarget.report.id,
        patch: { folderId: newParentId },
      });
    }
    setMoveTarget(null);
  }

  const foldersById = useMemo(() => {
    const map = new Map<string, Folder>();
    for (const f of foldersQ.data ?? []) map.set(f.id, f);
    return map;
  }, [foldersQ.data]);
  const templatesById = useMemo(() => {
    const map = new Map<string, ReportTemplateSummary>();
    for (const t of templates.data ?? []) map.set(t.id, t);
    return map;
  }, [templates.data]);

  const selectedReport = useMemo(
    () => reports.data?.find((r) => r.id === selected) ?? null,
    [reports.data, selected],
  );
  const total = reports.data?.length ?? 0;

  return (
    <>
      <div
        className="split-layout"
        data-sidebar={listSidebar.mode}
        // Only emit the inline width when the sidebar is expanded.
        // Inline styles have higher specificity than CSS rules, so
        // emitting it during collapse would clobber the rule that
        // sets --list-sidebar-width to 0 — the section column never
        // actually shrinks and the detail content stays offset.
        style={
          listSidebar.mode === 'expanded'
            ? ({ '--list-sidebar-width': `${listSidebarWidth.width}px` } as CSSProperties)
            : undefined
        }
      >
        <aside className="split-sidebar">
          <div className="split-sidebar-header">
            <span>Reports · {total}</span>
            <button
              type="button"
              className="btn-icon"
              onClick={listSidebar.toggle}
              title="Collapse list"
              aria-label="Collapse list"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10 4L6 8l4 4" />
              </svg>
            </button>
          </div>

          <div className="split-sidebar-toolbar">
            <div className="split-sidebar-search">
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              >
                <circle cx="7" cy="7" r="4.5" />
                <path d="M13.5 13.5l-3-3" />
              </svg>
              <input
                type="search"
                placeholder="Search folders + reports…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search"
              />
              {search && (
                <button
                  type="button"
                  className="btn-icon split-sidebar-clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => handleNewFolder(null)}
              title="Create a top-level folder"
              style={{
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M1.5 5a1.5 1.5 0 0 1 1.5-1.5h3.1a1.5 1.5 0 0 1 1.05.43L8.41 5H13a1.5 1.5 0 0 1 1.5 1.5v5.5a1.5 1.5 0 0 1-1.5 1.5H3a1.5 1.5 0 0 1-1.5-1.5V5z" />
                <path d="M8 7v3.5M6.25 8.75h3.5" />
              </svg>
              New folder
            </button>
          </div>

          {reports.isLoading && (
            <div className="text-dim text-sm" style={{ padding: 'var(--space-3)' }}>
              Loading…
            </div>
          )}
          {reports.error && (
            <div className="alert alert--danger" style={{ margin: 'var(--space-3)' }}>
              Failed to load reports.
            </div>
          )}
          {reports.data && total === 0 && !reports.isLoading && (
            <div className="empty-state" style={{ border: 'none', padding: 'var(--space-5)' }}>
              <div className="empty-state-icon">◰</div>
              <h3>No reports yet</h3>
              <p>
                Reports are configured, scheduled instances of a template. Open a template and
                choose <em>Clone as report…</em> to create your first one.
              </p>
              <Link className="btn btn--primary btn--sm" to="/templates">
                Browse templates
              </Link>
            </div>
          )}

          <ReportsFolderTree
            folders={foldersQ.data ?? []}
            reports={reports.data ?? []}
            templatesById={templatesById}
            selectedReportId={selected}
            onSelectReport={selectReport}
            expanded={expanded}
            onToggle={toggleFolder}
            searchQuery={search}
            onRenameFolder={handleRenameFolder}
            onMoveFolder={handleMoveFolder}
            onDeleteFolder={handleDeleteFolder}
            onNewFolder={handleNewFolder}
            onReorderFolder={handleReorderFolder}
            onRenameReport={handleRenameReport}
            onMoveReport={handleMoveReport}
            onCloneReport={handleCloneReport}
            onDeleteReport={handleDeleteReport}
            onRunReport={handleRunReport}
            onReorderReport={handleReorderReport}
            onDropReportToFolder={handleDropReportToFolder}
            onDropFolderToFolder={handleDropFolderToFolder}
          />
        </aside>

        {listSidebar.mode === 'expanded' && (
          <div
            className="split-sidebar-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize list panel"
            onDoubleClick={listSidebarWidth.reset}
            {...listSidebarWidth.bindResizer()}
            title="Drag to resize · double-click to reset"
          />
        )}

        <section>
          {listSidebar.mode === 'collapsed' && (
            <button
              type="button"
              className="split-sidebar-reopen"
              onClick={listSidebar.toggle}
              title="Open list"
              aria-label="Open list"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 4l4 4-4 4" />
              </svg>
              Reports ({total})
            </button>
          )}
          {selectedReport ? (
            <ReportDetail
              report={selectedReport}
              foldersById={foldersById}
              templateName={
                templatesById.get(selectedReport.templateId)?.name ?? '(unknown template)'
              }
              onRename={() => handleRenameReport(selectedReport)}
              onMove={() => handleMoveReport(selectedReport)}
              onClone={() => handleCloneReport(selectedReport)}
              onDelete={() => handleDeleteReport(selectedReport)}
              onRun={() => handleRunReport(selectedReport)}
              onSaveDefaults={async (values) => {
                await updateReportM.mutateAsync({
                  id: selectedReport.id,
                  patch: { defaultParameters: values },
                });
              }}
              onRenderStart={() => listSidebar.set('collapsed')}
            />
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">◰</div>
              <h3>Select a report</h3>
              <p>
                Pick a report on the left to see its template binding, default parameters, schedule,
                and delivery.
              </p>
            </div>
          )}
        </section>
      </div>

      {moveTarget && (
        <MoveToFolderDialog
          sourceLabel={
            moveTarget.kind === 'folder'
              ? `folder "${moveTarget.folder.name}"`
              : `report "${moveTarget.report.name}"`
          }
          folders={foldersQ.data ?? []}
          foldersById={foldersById}
          disallowId={moveTarget.kind === 'folder' ? moveTarget.folder.id : undefined}
          onCancel={() => setMoveTarget(null)}
          onChoose={handleMoveTargetChosen}
        />
      )}
      {promptDialog && <PromptDialog {...promptDialog} onCancel={() => setPromptDialog(null)} />}
      {confirmDialog && (
        <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog(null)} />
      )}
    </>
  );
}

type MoveTargetState =
  | { kind: 'folder'; folder: Folder }
  | { kind: 'report'; report: ReportSummary };

type PromptState = {
  title: string;
  description?: string | undefined;
  label: string;
  initialValue?: string | undefined;
  placeholder?: string | undefined;
  submitLabel?: string | undefined;
  destructive?: boolean | undefined;
  onSubmit: (value: string) => Promise<void>;
};
type ConfirmState = {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;
  destructive?: boolean | undefined;
  requireTyping?: string | undefined;
  onConfirm: () => Promise<void>;
};

// ── Detail pane ───────────────────────────────────────────────────────────

interface ReportDetailProps {
  report: ReportSummary;
  foldersById: Map<string, Folder>;
  templateName: string;
  onRename(): void;
  onMove(): void;
  onClone(): void;
  onDelete(): void;
  onRun(): void;
  onSaveDefaults(values: Record<string, unknown>): Promise<void>;
  /** Auto-collapse the list sidebar when a render kicks off — the
   *  user has signalled intent to focus on the report, not the list. */
  onRenderStart(): void;
}
function ReportDetail(props: ReportDetailProps): JSX.Element {
  const { report, foldersById, templateName, onRename, onMove, onClone, onDelete } = props;
  const templateQ = useReportTemplate(report.templateId);
  const [tab, setTab] = useState<'view' | 'settings'>('view');
  const [shareOpen, setShareOpen] = useState(false);
  // Most recent successful HTML run is the snapshot pin candidate. We
  // pull a small recent slice and pick the first matching one.
  const runsQ = useTemplateRuns(report.templateId, 25);
  const latestSuccessfulHtmlRunId = useMemo(() => {
    const runs = runsQ.data ?? [];
    // report_runs.status is 'succeeded' (past tense) once a render
    // completes, not 'success'. This match is what gates the snapshot
    // pin candidate in the Share modal.
    const match = runs.find((r) => r.status === 'succeeded' && r.format === 'html');
    return match?.id ?? null;
  }, [runsQ.data]);

  const folderPath = useMemo(() => {
    if (!report.folderId) return 'Top level';
    const chain: string[] = [];
    let cursor: string | null = report.folderId;
    while (cursor) {
      const f = foldersById.get(cursor);
      if (!f) break;
      chain.unshift(f.name);
      cursor = f.parentId;
    }
    return chain.length ? chain.join(' / ') : 'Top level';
  }, [report.folderId, foldersById]);

  const paramEntries = Object.entries(report.defaultParameters ?? {});

  // Unified inline header used as RenderPanel's leftSlot on the View
  // tab and as a standalone row on the Settings tab. Title + breadcrumb
  // + tabs + actions menu — no separate hero strip.
  const inlineHeader = (
    <InlineReportHeader
      name={report.name}
      folderPath={folderPath}
      templateId={report.templateId}
      templateName={stripJasperTag(templateName)}
      activeTab={tab}
      onTabChange={setTab}
      onClone={onClone}
      onMove={onMove}
      onRename={onRename}
      onDelete={onDelete}
      onShare={() => setShareOpen(true)}
    />
  );

  return (
    <div className={`report-detail ${tab === 'view' ? 'report-detail--full' : ''}`}>
      {shareOpen && (
        <SharePanel
          reportId={report.id}
          latestSuccessfulHtmlRunId={latestSuccessfulHtmlRunId}
          onClose={() => setShareOpen(false)}
        />
      )}
      {tab === 'view' ? (
        <RenderPanel
          key={report.id}
          templateId={report.templateId}
          templateName={report.name}
          reportId={report.id}
          initialParams={(() => {
            // Allow drill-through: any ?Foo=bar in the URL overrides the
            // instance's saved defaultParameters for that one render.
            // The saved defaults remain untouched — overrides are
            // session-only. Used by Parts Catalog → Birth Certificate
            // drill, and any other "open this report scoped to X" link.
            const saved = (report.defaultParameters ?? {}) as Record<string, unknown>;
            const overrides: Record<string, unknown> = {};
            new URLSearchParams(window.location.search).forEach((value, key) => {
              overrides[key] = value;
            });
            return { ...saved, ...overrides };
          })()}
          onSaveDefaults={props.onSaveDefaults}
          onRenderStart={props.onRenderStart}
          leftSlot={inlineHeader}
          pageOrientation={
            (report.pageOrientation ?? templateQ.data?.pageOrientation ?? 'portrait') as
              | 'portrait'
              | 'landscape'
          }
          pageSize={
            (report.pageSize ?? templateQ.data?.pageSize ?? 'letter') as
              | 'letter'
              | 'legal'
              | 'a4'
              | 'tabloid'
          }
        />
      ) : (
        <>
          {inlineHeader}
          <div className="detail-grid">
            <section className="detail-card">
              <h3 className="detail-card-title">Default parameters</h3>
              <p className="detail-card-hint">
                These values pre-fill the View tab's parameter form. The user can still override per
                run. Edit on the template's Render tab
                {templateQ.data?.currentVersion?.definition?.parameters?.length
                  ? ' — full parameter list lives there.'
                  : '.'}
              </p>
              {paramEntries.length > 0 ? (
                <table className="report-detail-params">
                  <tbody>
                    {paramEntries.map(([k, v]) => (
                      <tr key={k}>
                        <th>{k}</th>
                        <td>
                          <code>{JSON.stringify(v)}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-dim text-sm">
                  No defaults set. Every parameter is asked at run time.
                </div>
              )}
            </section>

            <ScheduleCard scheduleId={report.scheduleId} />
            <DeliveryCard scheduleId={report.scheduleId} fallbackDelivery={report.deliveryConfig} />

            <section className="detail-card">
              <h3 className="detail-card-title">Audience &amp; layout</h3>
              <dl className="detail-card-dl">
                <dt>Audience</dt>
                <dd>
                  {report.audience ?? <span className="text-dim">inherits from template</span>}
                </dd>
                <dt>Page size</dt>
                <dd>{report.pageSize ?? <span className="text-dim">inherits</span>}</dd>
                <dt>Orientation</dt>
                <dd>{report.pageOrientation ?? <span className="text-dim">inherits</span>}</dd>
              </dl>
            </section>

            <section className="detail-card">
              <h3 className="detail-card-title">Metadata</h3>
              <dl className="detail-card-dl">
                <dt>Created</dt>
                <dd>{new Date(report.createdAt).toLocaleString()}</dd>
                <dt>Updated</dt>
                <dd>{new Date(report.updatedAt).toLocaleString()}</dd>
                <dt>Report id</dt>
                <dd>
                  <code>{report.id}</code>
                </dd>
                <dt>Template id</dt>
                <dd>
                  <code>{report.templateId}</code>
                </dd>
              </dl>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

// ── Inline header ─────────────────────────────────────────────────────────
// One compact strip: report name + folder breadcrumb + template link,
// a View/Settings tab pill switcher, and an actions kebab. Slotted into
// RenderPanel's toolbar on the View tab; rendered directly above the
// detail grid on the Settings tab.
interface InlineReportHeaderProps {
  name: string;
  folderPath: string;
  templateId: string;
  templateName: string;
  activeTab: 'view' | 'settings';
  onTabChange(t: 'view' | 'settings'): void;
  onClone(): void;
  onMove(): void;
  onRename(): void;
  onDelete(): void;
  onShare(): void;
}
function InlineReportHeader(p: InlineReportHeaderProps): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent): void => {
      if (!(e.target as HTMLElement).closest('.inline-header-menu')) setMenuOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menuOpen]);

  return (
    <div className="inline-header">
      <div className="inline-header-title">
        <div className="inline-header-name" title={p.name}>
          {p.name}
        </div>
        <div className="inline-header-sub">
          <span>{p.folderPath}</span>
          <span className="text-dim">·</span>
          <Link to={`/templates/${p.templateId}`} className="text-dim">
            Template: {p.templateName}
          </Link>
        </div>
      </div>

      <div className="inline-header-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={p.activeTab === 'view'}
          className={`inline-header-tab ${p.activeTab === 'view' ? 'inline-header-tab--active' : ''}`}
          onClick={() => p.onTabChange('view')}
        >
          View
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={p.activeTab === 'settings'}
          className={`inline-header-tab ${p.activeTab === 'settings' ? 'inline-header-tab--active' : ''}`}
          onClick={() => p.onTabChange('settings')}
        >
          Settings
        </button>
      </div>

      <button
        type="button"
        className="btn-share"
        onClick={p.onShare}
        title="Share this report via a public link"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="4" cy="8" r="2" />
          <circle cx="12" cy="3.5" r="2" />
          <circle cx="12" cy="12.5" r="2" />
          <path d="M5.7 7.1l4.6-2.6" />
          <path d="M5.7 8.9l4.6 2.6" />
        </svg>
        <span>Share</span>
      </button>

      <div className="inline-header-menu">
        <button
          type="button"
          className="btn-icon"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          aria-expanded={menuOpen}
          aria-label="More actions"
          title="More actions"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
            <circle cx="3" cy="7" r="1.3" />
            <circle cx="7" cy="7" r="1.3" />
            <circle cx="11" cy="7" r="1.3" />
          </svg>
        </button>
        {menuOpen && (
          <div className="inline-header-menu-pop" role="menu">
            <button
              type="button"
              role="menuitem"
              className="inline-header-menu-item"
              onClick={() => {
                setMenuOpen(false);
                p.onClone();
              }}
            >
              Duplicate…
            </button>
            <button
              type="button"
              role="menuitem"
              className="inline-header-menu-item"
              onClick={() => {
                setMenuOpen(false);
                p.onMove();
              }}
            >
              Move…
            </button>
            <button
              type="button"
              role="menuitem"
              className="inline-header-menu-item"
              onClick={() => {
                setMenuOpen(false);
                p.onRename();
              }}
            >
              Rename…
            </button>
            <button
              type="button"
              role="menuitem"
              className="inline-header-menu-item inline-header-menu-item--danger"
              onClick={() => {
                setMenuOpen(false);
                p.onDelete();
              }}
            >
              Delete report
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function hasDelivery(d: Record<string, unknown>): boolean {
  return Object.keys(d ?? {}).length > 0;
}

function ScheduleCard({ scheduleId }: { scheduleId: string | null }): JSX.Element {
  const { data: schedule, isLoading } = useSchedule(scheduleId);
  if (!scheduleId) {
    return (
      <section className="detail-card">
        <h3 className="detail-card-title">Schedule</h3>
        <div className="text-dim text-sm">
          No schedule attached. Attach one in <Link to="/admin/schedules">Admin → Schedules</Link>.
        </div>
      </section>
    );
  }
  return (
    <section className="detail-card">
      <h3 className="detail-card-title">Schedule</h3>
      {isLoading || !schedule ? (
        <div className="text-dim text-sm">Loading…</div>
      ) : (
        <>
          <div className="schedule-card-head">
            <span
              className={`status-pill ${schedule.enabled ? 'status-pill--on' : 'status-pill--off'}`}
            >
              {schedule.enabled ? 'Enabled' : 'Paused'}
            </span>
            <strong>{schedule.name}</strong>
          </div>
          <dl className="detail-card-dl">
            <dt>Cron</dt>
            <dd>
              <code>{schedule.cron}</code> <span className="text-dim">({schedule.tz})</span>
            </dd>
            <dt>Next run</dt>
            <dd>
              {schedule.nextRunAt ? (
                new Date(schedule.nextRunAt).toLocaleString()
              ) : (
                <span className="text-dim">—</span>
              )}
            </dd>
            <dt>Last run</dt>
            <dd>
              {schedule.lastRunAt ? (
                <>
                  {new Date(schedule.lastRunAt).toLocaleString()}{' '}
                  <span className="text-dim">· {schedule.lastStatus ?? 'unknown'}</span>
                </>
              ) : (
                <span className="text-dim">never</span>
              )}
            </dd>
            <dt>Format</dt>
            <dd>
              {schedule.delivery?.format?.toUpperCase() ?? <span className="text-dim">—</span>}
            </dd>
          </dl>
          <div style={{ marginTop: 'var(--space-3)' }}>
            <Link to="/admin/schedules" className="btn-link btn-link--sm">
              Manage in Admin → Schedules →
            </Link>
          </div>
        </>
      )}
    </section>
  );
}

function DeliveryCard({
  scheduleId,
  fallbackDelivery,
}: {
  scheduleId: string | null;
  fallbackDelivery: Record<string, unknown>;
}): JSX.Element {
  const { data: schedule } = useSchedule(scheduleId);
  // Prefer the schedule's channels (that's where real delivery wiring
  // lives today); fall back to the report's own deliveryConfig field
  // for instances that haven't been scheduled yet.
  const channels = schedule?.delivery?.channels ?? [];
  const hasFallback = hasDelivery(fallbackDelivery);

  if (channels.length === 0 && !hasFallback) {
    return (
      <section className="detail-card">
        <h3 className="detail-card-title">Delivery</h3>
        <div className="text-dim text-sm">
          No delivery targets configured. When a schedule is attached, its email / webhook channels
          show up here.
        </div>
      </section>
    );
  }
  return (
    <section className="detail-card">
      <h3 className="detail-card-title">Delivery</h3>
      {channels.length > 0 ? (
        <ul className="delivery-channels">
          {channels.map((c, i) => (
            <li key={i} className={`delivery-channel delivery-channel--${c.kind}`}>
              <span className="delivery-channel-kind">{c.kind.toUpperCase()}</span>
              {c.kind === 'email' && (
                <span className="delivery-channel-body">
                  {c.recipients.join(', ')}
                  {c.subject && <span className="text-dim"> · "{c.subject}"</span>}
                </span>
              )}
              {c.kind === 'webhook' && (
                <span className="delivery-channel-body">
                  <code>{c.url}</code>
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <pre className="report-detail-delivery">{JSON.stringify(fallbackDelivery, null, 2)}</pre>
      )}
    </section>
  );
}
function stripJasperTag(name: string): string {
  return name.replace(/\s*\[Jasper #\d+\]\s*$/, '');
}
